import { type ParsedRole, parseRole, type Side, sideOf } from "@kmu-trexpert/shared";

import { readIamSessionToken } from "./session-token";
import { IAM_APP_ID, TREXPERT_PORTAL_APP_ID } from "./index";

/**
 * Role-based menu gating for IAM mode. Reads the signed-in user's roles from the
 * session token (the JWT `resource_access` claim) and decides which menu items
 * they may see.
 *
 * The IAM touchpoint → required-roles mapping is copied verbatim from iam-cds's
 * `managementTouchpoints` (packages/server/src/api/micro-frontend/index.ts) — the
 * authoritative declaration each Magellan management function gates on. Keep it
 * in sync if iam-cds changes those role requirements.
 *
 * Role matching is suffix-based: the token carries org-scoped roles in the
 * materialized form `org.{orgId}.iam.org-organization-admin`, while the required
 * lists use the bare source role `iam.org-organization-admin`. A user role
 * satisfies a requirement when it equals it (global roles like `iam.super-admin`)
 * or ends with `.{requirement}` (org-scoped roles) — mirroring iam-cds's
 * `Identity.hasAnySourceRoleAnywhere`.
 */

interface ResourceAccess {
    [appId: string]: { roles?: string[] } | undefined;
}

/** Decode a JWT payload without verifying the signature (read-only, client-side). */
const decodeJwtPayload = (token: string): { resource_access?: ResourceAccess } | undefined => {
    const payloadBase64 = token.split(".")[1];
    if (!payloadBase64) {
        return undefined;
    }
    try {
        const normalized = payloadBase64.replace(/-/g, "+").replace(/_/g, "/");
        const json = atob(normalized);
        return JSON.parse(json) as { resource_access?: ResourceAccess };
    } catch {
        return undefined;
    }
};

/** The roles the user carries for the given app audience (from `resource_access`). */
const rolesForAudience = (appId: string): string[] => {
    const token = readIamSessionToken();
    if (!token) {
        return [];
    }
    return decodeJwtPayload(token)?.resource_access?.[appId]?.roles ?? [];
};

/** True when any of the user's roles satisfies any required role (suffix match). */
const hasAnyRole = (userRoles: readonly string[], requiredRoles: readonly string[]): boolean =>
    requiredRoles.some(required =>
        userRoles.some(role => role === required || role.endsWith(`.${required}`))
    );

/**
 * IAM management touchpoint → required roles (any-of). Copied from iam-cds's
 * `managementTouchpoints`. Touchpoints absent here (e.g. `profile-view`) need
 * only an authenticated session.
 */
const IAM_TOUCHPOINT_ROLES: Record<string, readonly string[]> = {
    "user-management": [
        "iam.super-admin",
        "iam.user-admin",
        "iam.org-user-admin",
        "iam.org-organization-admin",
    ],
    "organization-management": ["iam.super-admin", "iam.organization-admin", "iam.org-organization-admin"],
    "organization-template-management": ["iam.super-admin", "iam.template-admin"],
    "application-management": ["iam.super-admin", "iam.template-admin"],
    "identity-provider-management": ["iam.super-admin"],
};

/**
 * Whether the signed-in user may see the menu item for an IAM touchpoint.
 * Touchpoints with no role requirement (e.g. `profile-view`) are always visible
 * to an authenticated user.
 */
export const canAccessIamTouchpoint = (touchpoint: string): boolean => {
    const required = IAM_TOUCHPOINT_ROLES[touchpoint];
    if (!required) {
        return true;
    }
    return hasAnyRole(rolesForAudience(IAM_APP_ID), required);
};

/**
 * The signed-in user's first trexpert-portal persona role, parsed into its org
 * + persona — the same parse the server applies (`parseRole`). `undefined` when
 * the user has no trexpert-portal role.
 */
const currentTrexpertRole = (): ParsedRole | undefined => {
    for (const role of rolesForAudience(TREXPERT_PORTAL_APP_ID)) {
        const parsed = parseRole(role);
        if (parsed) {
            return parsed;
        }
    }
    return undefined;
};

/**
 * The side (`trexpert` | `customer`) the signed-in user acts on. Drives which
 * app nav + routes the user sees, so a customer is never shown trustee-only
 * items (Mandate list, …) that would 403.
 */
export const currentTrexpertSide = (): Side | undefined => {
    const role = currentTrexpertRole();
    return role ? sideOf(role.persona) : undefined;
};

/**
 * The Mandate id for the signed-in customer's flat routes (Dateien / Aufgaben /
 * Belege). A customer has exactly one Mandate, whose id equals their
 * organisation id (1:1) — taken straight from the role's org segment, so no
 * lookup is needed. `undefined` for a trustee or an unauthenticated user.
 */
export const currentCustomerMandateId = (): string | undefined => {
    const role = currentTrexpertRole();
    return role && sideOf(role.persona) === "customer" ? role.organizationId : undefined;
};
